<template>
  <div class="seo-page news-post">
    <section class="seo-hero">
      <span class="seo-badge">{{ post.tag }}</span>
      <h1>{{ post.title }}</h1>
      <img
        v-if="headerImage && !imageFailed"
        class="news-post__image"
        :src="headerImage"
        :alt="post.games[0].name"
        loading="lazy"
        @error="imageFailed = true"
      >
      <p>{{ post.intro }}</p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Track your own playtime</router-link>
        <a class="seo-secondary-link" href="#our-numbers">See our players' numbers →</a>
      </div>
      <ul class="seo-highlight-list">
        <li v-for="highlight in post.highlights" :key="highlight">
          <span class="seo-highlight-icon">▸</span>
          <div>{{ highlight }}</div>
        </li>
      </ul>
    </section>

    <section v-for="section in post.sections" :key="section.heading" class="seo-section">
      <h2>{{ section.heading }}</h2>
      <p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
    </section>

    <section class="seo-section" id="our-numbers">
      <h2>{{ post.statsHeading }}</h2>
      <div class="seo-grid">
        <div v-for="stat in post.stats" :key="stat.label" class="seo-card">
          <h3>{{ stat.value }}</h3>
          <p><strong>{{ stat.label }}</strong></p>
          <p v-if="stat.note">{{ stat.note }}</p>
        </div>
      </div>
      <p class="news-post__fineprint">
        Figures are aggregated from the day-by-day Steam playtime of steamplayti.me players, a self-selected group of
        people who track their playtime, not all of Steam. No individual profile is shown or identifiable.
      </p>
    </section>
    <SeoRelatedTools heading="Track Your Own Steam Playtime" />
  </div>
</template>

<script>
import { applyMeta, articleStructuredData } from '../utils/meta';
import { newsPostUrl } from '../utils/newsPosts';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';

export default {
  name: 'NewsPost',
  components: { SeoRelatedTools },
  props: {
    post: { type: Object, required: true },
  },
  data() {
    return { imageFailed: false };
  },
  computed: {
    headerImage() {
      const appId = this.post.games[0]?.appId;

      return appId
        ? `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${appId}/header.jpg`
        : null;
    },
  },
  mounted() {
    const canonical = newsPostUrl(this.post);

    applyMeta({
      title: `${this.post.title} | steamplayti.me`,
      description: this.post.description,
      canonical,
      ogTitle: this.post.title,
      ogDescription: this.post.description,
      ogUrl: canonical,
      twitterTitle: this.post.title,
      twitterDescription: this.post.description,
      structuredData: articleStructuredData({
        headline: this.post.title,
        description: this.post.description,
        canonical,
        datePublished: this.post.date,
        breadcrumbs: [
          { name: 'Home', path: '/' },
          { name: 'Tools & Blog', path: '/blog/' },
          { name: this.post.title, path: canonical },
        ],
      }),
    });
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style scoped>
.news-post {
  background: radial-gradient(circle at top left, rgba(102, 192, 244, 0.16), transparent 65%);
}

/* Titles are model-written: a long unbreakable word ("steamplayti.me" at h1
   size) widened the whole page ~28px past a phone viewport. */
.news-post h1,
.news-post h2,
.news-post h3 {
  overflow-wrap: anywhere;
}

.news-post__image {
  width: 100%;
  max-width: 460px;
  border-radius: 12px;
  border: 1px solid rgba(102, 192, 244, 0.12);
}

.news-post .seo-badge {
  align-self: flex-start;
}

.news-post__fineprint {
  font-size: 0.9rem;
}
</style>
